import React, { useState } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import PasswordField from '../../common/PasswordField';

interface Step3CompleteProfileProps {
  email: string;
  onComplete: () => void;
  onBack: () => void;
}

const Step3CompleteProfile: React.FC<Step3CompleteProfileProps> = ({ email, onComplete, onBack }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companySize: '',
    role: '',
    password: '',
    confirmPassword: '',
    emailNotifications: true
  });
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    firstName: '',
    lastName: '',
    companySize: '',
    role: '',
    password: '',
    confirmPassword: ''
  });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const roles = [
    'UX/UI Designer',
    'Researcher',
    'Strategist',
    'Service Designer',
    'Accessibility Specialist',
    'Content Strategist',
    'Program Manager',
    'Developer',
    'Product Manager',
    'Product Owner',
    'Design Manager',
    'Creative Director',
    'Brand Strategist',
    'QA / Tester',
    'Other'
  ];

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/\d/.test(password)) return false;
    if (!/[^A-Za-z\d\s]/.test(password)) return false;
    return true;
  };

  const isFormValid = (): boolean => {
    if (!formData.firstName || formData.firstName.trim().length < 3) return false;
    if (!formData.lastName || formData.lastName.trim().length < 1) return false;
    if (!formData.companySize) return false;
    if (!formData.role) return false;
    if (!formData.password) return false;
    if (!validatePassword(formData.password)) return false;
    if (!formData.confirmPassword) return false;
    if (formData.password !== formData.confirmPassword) return false;

    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    setFieldErrors(prev => ({
      ...prev,
      [name]: ''
    }));

  };

  const handleFirstNameBlur = () => {
    if (formData.firstName && formData.firstName.trim().length > 0 && formData.firstName.trim().length < 3) {
      setFieldErrors(prev => ({ ...prev, firstName: 'FIRST NAME MUST BE AT LEAST 3 CHARACTERS' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({
      firstName: '',
      lastName: '',
      companySize: '',
      role: '',
      password: '',
      confirmPassword: ''
    });

    let hasErrors = false;

    // Validate required fields
    if (!formData.firstName || formData.firstName.trim().length < 1) {
      setFieldErrors(prev => ({ ...prev, firstName: 'First name is required' }));
      hasErrors = true;
    } else if (formData.firstName.trim().length < 3) {
      setFieldErrors(prev => ({ ...prev, firstName: 'FIRST NAME MUST BE AT LEAST 3 CHARACTERS' }));
      hasErrors = true;
    }

    if (!formData.lastName || formData.lastName.trim().length < 1) {
      setFieldErrors(prev => ({ ...prev, lastName: 'Last name is required' }));
      hasErrors = true;
    }

    if (!formData.companySize) {
      setFieldErrors(prev => ({ ...prev, companySize: 'Company size is required' }));
      hasErrors = true;
    }

    if (!formData.role) {
      setFieldErrors(prev => ({ ...prev, role: 'Please select your role' }));
      hasErrors = true;
    }

    if (!formData.password) {
      setFieldErrors(prev => ({ ...prev, password: 'Password is required' }));
      hasErrors = true;
    }

    if (!validatePassword(formData.password)) {
      setFieldErrors(prev => ({ ...prev, password: 'Password does not meet requirements' }));
      hasErrors = true;
    }

    if (!formData.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: 'Please confirm your password' }));
      hasErrors = true;
    } else if (formData.password !== formData.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
      hasErrors = true;
    }

    if (hasErrors) return;

    setShowTermsModal(true);
  };

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    setLoading(true);

    try {
      await api.post('/api/register/complete', {
        email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companySize: formData.companySize,
        role: formData.role,
        emailNotifications: formData.emailNotifications
      });

      // Sign in immediately after registration
      await signIn(email, formData.password, false);

      // Navigate to home — user is now authenticated on the Starter plan
      onComplete();
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to complete registration. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <h1 className="text-center">Email address verified</h1>
      <h2 className="text-center text-muted mb-4">Finalize your account setup.</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group className="mb-3">
          <Form.Label>First Name <span className="required-label">*</span></Form.Label>
          <Form.Control
            type="text"
            name="firstName"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={handleInputChange}
            onBlur={handleFirstNameBlur}
            isInvalid={!!fieldErrors.firstName}
          />
          {fieldErrors.firstName && (
            <Form.Text className="text-danger form-error-text-normal">
              {fieldErrors.firstName}
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Last Name <span className="required-label">*</span></Form.Label>
          <Form.Control
            type="text"
            name="lastName"
            placeholder="Enter your last name"
            value={formData.lastName}
            onChange={handleInputChange}
            isInvalid={!!fieldErrors.lastName}
          />
          {fieldErrors.lastName && <Form.Text className="text-danger">{fieldErrors.lastName}</Form.Text>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Company Size <span className="required-label">*</span></Form.Label>
          <Form.Select
            name="companySize"
            value={formData.companySize}
            onChange={handleInputChange}
            isInvalid={!!fieldErrors.companySize}
          >
            <option value="">Select company size</option>
            {companySizes.map(size => (
              <option key={size} value={size}>{size} employees</option>
            ))}
          </Form.Select>
          {fieldErrors.companySize && <Form.Text className="text-danger">{fieldErrors.companySize}</Form.Text>}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>What is your role? <span className="required-label">*</span></Form.Label>
          <Form.Select
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            isInvalid={!!fieldErrors.role}
          >
            <option value="">Select your role</option>
            {roles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </Form.Select>
          {fieldErrors.role && <Form.Text className="text-danger">{fieldErrors.role}</Form.Text>}
        </Form.Group>

        <PasswordField
          label="Password"
          value={formData.password}
          onChange={(value) => {
            setFormData(prev => ({ ...prev, password: value }));
            setFieldErrors(prev => ({ ...prev, password: '' }));
          }}
          placeholder="Enter your password"
          error={fieldErrors.password}
          disabled={false}
        />

        {formData.password.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Confirm Password <span className="required-label">*</span></Form.Label>
            <div className="password-field-container">
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.confirmPassword}
                className="password-field-padded"
              />
              <Button
                variant="link"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                type="button"
                className="password-toggle-btn"
              >
                {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </Button>
            </div>
            {fieldErrors.confirmPassword && <Form.Text className="text-danger">{fieldErrors.confirmPassword}</Form.Text>}
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="emailNotifications"
            name="emailNotifications"
            label="Receive email notifications"
            checked={formData.emailNotifications}
            onChange={(e) => setFormData(prev => ({ ...prev, emailNotifications: e.target.checked }))}
            className="text-start"
          />
        </Form.Group>

        <Button
          type="submit"
          className="w-100 mb-3"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'REGISTERING...' : 'REGISTER'}
        </Button>
      </Form>

      <Modal
        show={showTermsModal}
        onHide={() => {}}
        centered
        backdrop="static"
        keyboard={false}
        className="terms-modal"
        backdropClassName="terms-modal-backdrop"
      >
        <Modal.Body>
          <h5>Terms of Service</h5>
          <p>
            Before proceeding, please review our Terms of Service. These terms outline our commitments to you and the responsibilities we both share. We've designed them with precision and clarity to ensure a powerful, controlled experience.
          </p>
          <p>
            By accepting these terms, you are acknowledging you've read and reviewed our Terms of Service.
          </p>
          <Form.Group className="mt-4">
            <Form.Check
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              label={
                <p>
                  I accept Revealer's <Link to="/terms" target="_blank">Terms of Service</Link>
                </p>
              }
            />
          </Form.Group>
          <br />
          <Button
            variant="primary"
            onClick={handleTermsAccepted}
            disabled={!termsAccepted}
            className="terms-modal-continue-btn"
          >
            Continue
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Step3CompleteProfile;